// src/pages/ReleasesPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { useNotification } from '../hooks/useNotification';
import { getImageUrl } from '../utils/getImageUrl';

function ReleasesPage() {
  const { bookId } = useParams();
  const { notify, Notification } = useNotification();

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookName, setBookName] = useState('');

  // Fetch releases
  useEffect(() => {
    const fetchReleases = async () => {
      if (!bookId) return;
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`/api/book-releases/${bookId}`);
        const items = data?.data || data?.releases || [];
        setReleases(items);
        if (data?.book_name) setBookName(data.book_name);
      } catch (e) {
        console.error('Failed to fetch releases', e);
        setError('Failed to fetch releases.');
      } finally {
        setLoading(false);
      }
    };

    if (bookId) fetchReleases();
  }, [bookId]);

  const rows = useMemo(() => {
    const parsed = releases.map((r) => ({
      ...r,
      release_date_fmt: r.release_date
        ? new Date(r.release_date).toLocaleDateString()
        : '',
    }));
    parsed.sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
    return parsed;
  }, [releases]);

  const isEmpty = (v) => !v || String(v).trim().length === 0;

  const handleDelete = useCallback(
    async (releaseId) => {
      if (!releaseId) return;
      const confirmed = window.confirm(
        'Delete this release? This cannot be undone.',
      );
      if (!confirmed) return;
      try {
        await axios.delete(`/api/book-releases/${releaseId}`);
        setReleases((prev) => prev.filter((r) => r._id !== releaseId));
        notify('Release deleted', 'success');
      } catch (e) {
        console.error('Failed to delete release', e);
        notify('Failed to delete release', 'error');
      }
    },
    [notify],
  );

  return (
    <>
      <Notification />
      <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
        <div className="flex items-center text-xl font-bold mb-4">
          <BackButton className="p-2 rounded hover:bg-gray-200" />
          <span className="text-center flex-grow">
            {bookName || 'Releases'}
          </span>
        </div>

        {loading && <p className="text-center text-gray-500">Loading…</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              No releases at the moment
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Add a release when this book is ready to publish.
            </p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    Release date
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    Name
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    Description
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b whitespace-nowrap">
                    Language
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    Cover page
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    PDF
                  </th>
                  <th className="text-left font-semibold text-sm p-3 border-b">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className="border-t">
                    <td className="p-3 align-top text-sm">
                      {r.release_date_fmt}
                    </td>
                    <td className="p-3 align-top text-sm">
                      {isEmpty(r.name) ? <span className="text-gray-500">N/A</span> : r.name}
                    </td>
                    <td className="p-3 align-top text-sm max-w-xs">
                      {isEmpty(r.description) ? (
                        <span className="text-gray-500">N/A</span>
                      ) : (
                        <span className="text-gray-800 block truncate" title={r.description}>
                          {r.description}
                        </span>
                      )}
                    </td>
                    <td className="p-3 align-top text-sm">{r.language_code}</td>
                    <td className="p-3 align-top text-sm">
                      {isEmpty(r.cover_page_url) ? (
                        <span className="text-gray-500">N/A</span>
                      ) : (
                        <>
                          <a
                            href={getImageUrl(r.cover_page_url)}
                            className="text-blue-600 hover:underline break-all"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {r.cover_page_url}
                          </a>
                          <div className="mt-2">
                            <img
                              src={
                                getImageUrl
                                  ? getImageUrl(r.cover_page_url)
                                  : r.cover_page_url
                              }
                              alt="Cover preview"
                              className="h-16 w-auto rounded border border-gray-200 object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        </>
                      )}
                    </td>
                    <td className="p-3 align-top text-sm">
                      {isEmpty(r.release_pdf_url) ? (
                        <span className="text-gray-500">N/A</span>
                      ) : (
                        <a
                          href={getImageUrl(r.release_pdf_url)}
                          className="text-blue-600 hover:underline break-all"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {r.release_pdf_url}
                        </a>
                      )}
                    </td>
                    <td className="p-3 align-top text-sm">
                      <div className="flex gap-2">
                        <Link
                          to={`/book-releases/${r._id}/edit`}
                          className="bg-green-500 text-white px-5 py-1 rounded hover:bg-green-600 flex items-center justify-center" // Added flex, items-center, justify-center for text centering
                          title="Edit release"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(r._id)}
                          className="bg-red-500 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-red-600 hover:bg-red-50 text-white active:bg-red-100 transition-colors"
                          title="Delete release"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-between text-sm text-gray-600">
          <Link to="/" className="hover:underline">
            ← Back
          </Link>

          <Link
            to={`/create-book-release/${bookId}`}
            className="bg-white border px-3 py-1 rounded hover:bg-gray-100"
          >
            Add Release
          </Link>
        </div>
      </div>
    </>
  );
}

export default ReleasesPage;
